import {
	textToImageIcon,
	textToVideoIcon,
} from "@/comps/ai-tools/icons"
import { DropDown } from "@/comps/dropdown"
import { assetUrl } from "@/utils/cdn"
import { mutateWithNotify } from "@/utils/errors"
import { NotificationContext } from "@/utils/notification"
import axios from "axios"
import clsx from "clsx"
import { useTranslation } from "next-i18next"
import { DeforumStatusSingle } from "pages/api/deforum-status-new"
import { useContext, useState } from "react"
import { useQueryClient } from "react-query"

interface ActiveJobProps {
	video: Exclude<DeforumStatusSingle, null>
	isLast: boolean
}

export function ActiveJob(props: ActiveJobProps) {
	const {
		status,
		thumbnail,
		estimatedSeconds,
		id,
		isVideo,
		tool,
	} = props.video

	const [loading, setLoading] = useState(false)

	const ceil = Math.ceil(estimatedSeconds / 60)
	const minutes = ceil < 1 ? 1 : ceil

	const { t } = useTranslation()
	const { notify } = useContext(NotificationContext)

	const queryClient = useQueryClient()

	async function handleCancel() {
		setLoading(true)

		await mutateWithNotify(
			notify,
			() =>
				axios
					.post("/api/deforum-cancel", { id })
					.then((res) => res.data),
			t("txt_successfully_canceled"),
			t("txt_error_canceling"),
		)

		await queryClient.invalidateQueries("deforum-last")
		setLoading(false)
	}

	return (
		<div
			className={clsx(
				"relative flex h-[80px] justify-between hover:bg-blue-100",
				"group w-full shrink-0 rounded-[6px] p-[8px] text-blue-800",
			)}>
			<div className="flex items-center gap-[8px]">
				{isVideo && thumbnail ? (
					<video
						src={thumbnail}
						loop
						playsInline
						muted
						autoPlay
						className={clsx(
							"aspect-1 h-[64px] object-cover",
							"rounded-[5px]",
						)}
					/>
				) : thumbnail ? (
					<img
						src={thumbnail}
						alt="thumbnail"
						className={clsx(
							"aspect-1 h-[64px] object-cover",
							"rounded-[5px]",
						)}
					/>
				) : (
					<div
						className={clsx(
							tool === "text_to_image" &&
								"rounded-[6px] bg-blue-200",
							"flex aspect-1 h-full items-center justify-center",
							"[--icon-color:var(--color-blue-500)]",
						)}>
						{tool === "text_to_image" && textToImageIcon()}
						{tool === "text_to_video" && textToVideoIcon()}
						{tool === "ai_suggest" && (
							<img
								src={assetUrl("/general/model-icon.svg")}
								alt="model icon"
							/>
						)}
					</div>
				)}

				<div
					className={clsx(
						"flex h-full flex-col justify-center gap-1",
					)}>
					<p className=" text-[14px] text-blue-800">
						{t("txt_status")}
						<strong>
							{status === "in-progress"
								? t("txt_in_progress")
								: t("txt_in_queue")}
						</strong>
					</p>
					<p className=" text-[14px] text-blue-800">
						{t("txt_remaining")}
						<strong>
							{t("fs_minutes_short", { count: minutes })}
						</strong>
					</p>
				</div>
			</div>

			<DropDown
				className={clsx(
					status !== "in-queue" ? "hidden" : "flex",
				)}
				trigger={
					<div
						className={clsx(
							"h-[28px] w-[28px] rounded-[6px] bg-blue-100 group-hover:flex",
							"hidden items-center justify-center hover:bg-blue-200",
						)}>
						<img
							src={assetUrl("/general/more-gray.svg")}
							alt="more icon"
							className="w-[15px]"
						/>
					</div>
				}>
				<div
					className={clsx(
						props.isLast ? "top-[-55px]" : "top-[50px] ",
						"absolute right-0 w-[175px] border",
						"rounded-[8px] border-color-separator bg-color-popup-cell",
						"p-[10px] [box-shadow:0px_0px_10px_rgba(0,0,0,0.13)]",
					)}>
					<button
						onClick={handleCancel}
						disabled={loading}
						className={clsx(
							"rounded-[8px] text-[14px] hover:bg-blue-100",
							"px-[8px] py-[12px] font-500 text-blue-800",
							"w-full text-start disabled:pointer-events-none",
						)}>
						{t("lbl_cancel")}
					</button>
				</div>
			</DropDown>
		</div>
	)
}
